import cn from 'clsx';
import {useEffect, useMemo, useRef, useState} from 'react';
import ReactPlayer from 'react-player';
import screenfull from 'screenfull';

import NextArrow from '@/public/images/next-video-arror.svg';
import PreviousArrow from '@/public/images/prev-video-arror.svg';
import ArrowControlNext from '@/public/images/v-control-arrow-next.svg';
import ArrowControlPrev from '@/public/images/v-control-arrow-prev.svg';
import FullscreenIcon from '@/public/images/v-control-fullscreen.svg';
import MuteIcon from '@/public/images/v-control-muted.svg';
import PlayIcon from '@/public/images/v-control-play.svg';
import StoppedIcon from '@/public/images/v-control-stopped.svg';
import UnMuteIcon from '@/public/images/v-control-unmuted.svg';
import useIsMobile from '@/src/hooks/useIsMobile';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import LoadingIcon from '../../../../public/icons/loaing.svg';
import PlayIconLarge from '../../../../public/icons/playVideoIcon.svg';
import css from '../styles/VideoPlayer.module.css';
import Duration from './Duration';

interface VideoPlayerLayoutProps {
  url: string;
  durationInSeconds: number;
  videoPlayerRef: React.MutableRefObject<ReactPlayer | null>;
  videoWithControlsRef: React.MutableRefObject<HTMLDivElement | null>;
  isPlaying: boolean;
  isMuted: boolean;
  loaded: number;
  played: number;
  onSeekChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSeekMouseUp: (event: React.MouseEvent<HTMLInputElement>) => void;
  onSeekMouseDown: (event: React.MouseEvent<HTMLInputElement>) => void;
  onSetPlayingClick: () => void;
  onSetMutedClick: () => void;
  onFullScreenClick: () => void;
  onPreviousVideoClick: () => void;
  onNextVideoClick: () => void;
  isNextDisable: boolean;
  isPreviousDisable: boolean;
  onProgressChange: (state: {played: number; loaded: number}) => void;
  onVideoFinish: (...args: any) => void;
}

const VideoPlayerLayout = ({
  url,
  durationInSeconds,
  videoPlayerRef,
  videoWithControlsRef,
  isPlaying,
  isMuted,
  onProgressChange,
  loaded,
  played,
  onSeekChange,
  onSeekMouseUp,
  onSeekMouseDown,
  onSetPlayingClick,
  onSetMutedClick,
  onFullScreenClick,
  onPreviousVideoClick,
  onNextVideoClick,
  isNextDisable,
  isPreviousDisable,
  onVideoFinish,
}: VideoPlayerLayoutProps) => {
  const [isReady, setIsReady] = useState(false);
  const {isMaxWidth1100, isMaxWidth900} = useIsMobile();
  const [isFullscreenControls, setFullscreenControls] = useState(false);

  const cursorRef = useRef(null);

  useEffect(() => {
    setIsReady(false);
  }, [url]);

  const navigationButtons = useMemo(
    () => (
      <div className={css.navigationButtons}>
        <button
          disabled={isPreviousDisable}
          onClick={onPreviousVideoClick}
          className={cn(css.navigationButton, css.navigationButtonPrev)}
        >
          <PreviousArrow /> <TextLocal id={'create-course.back'} />
        </button>
        <button
          disabled={isNextDisable}
          onClick={onNextVideoClick}
          className={cn(css.navigationButton, css.navigationButtonNext)}
        >
          <TextLocal id={'create-course.next'} /> <NextArrow />
        </button>
      </div>
    ),
    [onNextVideoClick, onPreviousVideoClick, isNextDisable, isPreviousDisable]
  );

  const playVideoWrapperNode = useMemo(
    () => (
      <div className={css.iconButtonCenterWrapper}>
        <PlayIconLarge />
      </div>
    ),
    []
  );

  const onMouseMoveCursor = () => {
    if (cursorRef.current) {
      setFullscreenControls(true);
      clearTimeout(cursorRef.current);
    }

    cursorRef.current = setTimeout(() => {
      setFullscreenControls(false);
    }, 2000);
  };

  return (
    <>
      <div
        ref={videoWithControlsRef}
        onMouseMove={onMouseMoveCursor}
        className={cn(css.mainWrapper, {
          // @ts-ignore
          [css.mainWrapperFullSize]: screenfull.isFullscreen,
          [css.mainWrapperFullSizeActiveCursor]: isFullscreenControls,
        })}
      >
        <div className={css.relativePosition} onClick={onSetPlayingClick}>
          {!isReady && !isMaxWidth900 && (
            <div className={css.loadingIcon}>
              <LoadingIcon />
            </div>
          )}
          {isReady && !isPlaying && !isMaxWidth900 && playVideoWrapperNode}
          <ReactPlayer
            playsinline={isMaxWidth900}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                },
              },
            }}
            onContextMenu={(event) => event.preventDefault()}
            controls={isMaxWidth900}
            className={css.videoPlayer}
            ref={videoPlayerRef}
            url={url}
            width="100%"
            height="100%"
            playing={isPlaying}
            muted={isMuted}
            onReady={() => setIsReady(true)}
            onEnded={() => onVideoFinish(true)}
            onProgress={onProgressChange}
          />
        </div>
        {!isMaxWidth900 && (
          <div className={css.mainControls}>
            {!isMaxWidth1100 && navigationButtons}
            <label className={css.track}>
              <div
                className={css.loader}
                style={{
                  width: `${loaded * 100}%`,
                }}
              />
              <div
                className={css.currentTime}
                style={{
                  width: `${played * 100}%`,
                }}
              />

              <input
                type="range"
                min={0}
                max={0.999999}
                step="any"
                value={played || 0}
                onMouseDown={onSeekMouseDown}
                onChange={onSeekChange}
                onMouseUp={onSeekMouseUp}
              />
            </label>

            <div className={css.controls}>
              <div className={css.actions}>
                <button
                  disabled={isPreviousDisable}
                  className={css.controlButtons}
                  onClick={onPreviousVideoClick}
                >
                  <ArrowControlPrev />
                </button>
                <button
                  className={css.controlButtons}
                  onClick={onSetPlayingClick}
                >
                  {isPlaying ? <StoppedIcon /> : <PlayIcon />}
                </button>
                <button
                  className={css.controlButtons}
                  onClick={onNextVideoClick}
                  disabled={isNextDisable}
                >
                  <ArrowControlNext />
                </button>
              </div>
              <button
                className={cn(css.controlButtons, css.muteButton)}
                onClick={onSetMutedClick}
              >
                {isMuted ? <MuteIcon /> : <UnMuteIcon />}
              </button>

              <div className={css.time}>
                <span>
                  <Duration seconds={durationInSeconds * played || 0} />
                </span>
                /
                <span>
                  <Duration seconds={durationInSeconds || 0} />
                </span>
              </div>

              <button
                onClick={onFullScreenClick}
                className={cn(css.controlButtons, css.fullScreen)}
              >
                <FullscreenIcon />
              </button>
            </div>
          </div>
        )}
      </div>
      {isMaxWidth1100 && navigationButtons}
    </>
  );
};

export default VideoPlayerLayout;
